import React, { useEffect, useState } from 'react';
import { Percent, ArrowRight, Loader, Plus, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { usePartnerStore } from '../../../lib/stores/partnerStore';
import { useApp } from '../../AppProvider';
import { usePermissions } from '../../../lib/hooks/usePermissions';
import { SectionToggle } from '../../shared/activity-drawer/ui/SectionToggle';
import { PropRow } from '../../shared/activity-drawer/ui/PropRow';
import { notifyPartnerProfilesChanged } from '../../../lib/services/partnerReferrerProfilesService';
import {
  buildCommissionUpdate,
  formatReferrerCommission,
  type CommissionKind,
  type ReferrerProfileLite,
} from '../../../lib/partners/partnerReferrerProfile';
import type { ExternalPartner } from '../../../lib/types';

export interface PartnerCommissionsSectionProps {
  partner: ExternalPartner;
  open: boolean;
  onToggle(): void;
  reloadToken: number;
  onDataChanged(): void;
}

const INPUT = 'w-24 px-1.5 py-0.5 text-[13px] rounded border border-[rgba(55,53,47,0.16)] dark:border-white/15 bg-transparent text-[#37352F] dark:text-white focus:outline-none focus:border-[#2383E2]';

/**
 * Sezione "Provvigioni" del tab Info partner. Ogni partner ha il suo profilo
 * segnalatore (riga referrers collegata), creato insieme al partner: qui si
 * imposta la provvigione che riceve sulle offerte in cui e' segnalatore.
 * Per i partner nati prima resta "Crea profilo provvigioni" (provvigione 0).
 */
export function PartnerCommissionsSection({ partner, open, onToggle, reloadToken, onDataChanged }: PartnerCommissionsSectionProps): JSX.Element {
  const { generateReferrerFromPartner } = usePartnerStore();
  const { toast } = useApp();
  const { canEditPartner } = usePermissions();
  const [profile, setProfile] = useState<ReferrerProfileLite | null>(null);
  const [groupName, setGroupName] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  // Stato locale (non `usePartnerStore().loading`, alzato anche da fetch non correlate)
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState<CommissionKind>('percentage');
  const [value, setValue] = useState('');

  useEffect(() => {
    let cancelled = false;
    setChecking(true);
    (async () => {
      const { data } = await supabase
        .from('referrers')
        .select('id, external_partner_id, commission_rate, commission_type, commission_fixed_amount, referrer_group_id, created_at')
        .eq('external_partner_id', partner.id)
        .order('created_at', { ascending: true })
        .limit(1);
      const row = (data?.[0] as ReferrerProfileLite | undefined) || null;
      let group: string | null = null;
      if (row?.referrer_group_id) {
        const { data: g } = await supabase.from('referrer_groups').select('group_name').eq('id', row.referrer_group_id).maybeSingle();
        group = (g as { group_name?: string } | null)?.group_name || null;
      }
      if (cancelled) return;
      setProfile(row);
      setGroupName(group);
      const rowKind: CommissionKind = row?.commission_type === 'fixed' ? 'fixed' : 'percentage';
      setKind(rowKind);
      setValue(String(rowKind === 'fixed' ? row?.commission_fixed_amount ?? 0 : row?.commission_rate ?? 0));
      setChecking(false);
    })();
    return () => { cancelled = true; };
  }, [partner.id, reloadToken]);

  const handleCreate = async () => {
    setBusy(true);
    try {
      const result = await generateReferrerFromPartner(partner.id);
      if (result) {
        toast.success('Profilo provvigioni creato: il partner è selezionabile come segnalatore nelle offerte.');
        notifyPartnerProfilesChanged();
        onDataChanged();
      } else {
        toast.error('Non è stato possibile creare il profilo provvigioni. Riprova.');
      }
    } finally {
      setBusy(false);
    }
  };

  const save = async (nextKind: CommissionKind, raw: string) => {
    if (!profile) return;
    const result = buildCommissionUpdate(nextKind, raw);
    if ('error' in result) { toast.error(result.error); return; }
    // Nessuna modifica (es. blur senza aver cambiato nulla): niente scrittura
    const currentKind: CommissionKind = profile.commission_type === 'fixed' ? 'fixed' : 'percentage';
    const currentValue = Number(currentKind === 'fixed' ? profile.commission_fixed_amount ?? 0 : profile.commission_rate ?? 0);
    const nextValue = Number(nextKind === 'fixed' ? result.update.commission_fixed_amount : result.update.commission_rate);
    if (currentKind === nextKind && currentValue === nextValue) return;
    setBusy(true);
    const { data, error } = await supabase
      .from('referrers')
      .update({ ...result.update, updated_at: new Date().toISOString() })
      .eq('id', profile.id)
      .select('id, external_partner_id, commission_rate, commission_type, commission_fixed_amount, referrer_group_id, created_at')
      .single();
    setBusy(false);
    if (error) { toast.error('Provvigione non salvata. Riprova.'); return; }
    setProfile(data as ReferrerProfileLite);
    notifyPartnerProfilesChanged();
    toast.success('Provvigione aggiornata');
  };

  const changeKind = (next: CommissionKind) => {
    setKind(next);
    const nextValue = String(next === 'fixed' ? profile?.commission_fixed_amount ?? 0 : profile?.commission_rate ?? 0);
    setValue(nextValue);
    save(next, nextValue);
  };

  const summaryText = groupName ? `Gruppo ${groupName}` : formatReferrerCommission(profile);
  const summary = checking ? (
    <span className="text-[12px] font-medium text-[#37352F] dark:text-white">…</span>
  ) : summaryText ? (
    <span className="text-[12px] font-medium text-[#37352F] dark:text-white">{summaryText}</span>
  ) : (
    <span className="text-[12px] text-[rgba(55,53,47,0.45)] dark:text-white/40">Da impostare</span>
  );

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <SectionToggle
      open={open}
      onToggle={onToggle}
      icon={<Percent className="h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />}
      title="Provvigioni"
      summary={summary}
      dataTour="partner-info-commissions"
    >
      {checking ? null : profile ? (
        <div className="flex flex-col">
          {groupName && (
            <PropRow icon={<Users className="h-3 w-3" strokeWidth={1.5} />} label="Gruppo">
              <span title="Con un gruppo assegnato la provvigione arriva dalle regole del gruppo">{groupName}</span>
            </PropRow>
          )}
          <PropRow icon={<Percent className="h-3 w-3" strokeWidth={1.5} />} label="Provvigione">
            {canEditPartner ? (
              <span className="inline-flex items-center gap-1.5" onClick={stop}>
                <select
                  value={kind}
                  disabled={busy}
                  onChange={(e) => changeKind(e.target.value as CommissionKind)}
                  className="px-1 py-0.5 text-[13px] rounded border border-[rgba(55,53,47,0.16)] dark:border-white/15 bg-transparent text-[#37352F] dark:text-white dark:bg-gray-900"
                >
                  <option value="percentage">%</option>
                  <option value="fixed">€ fisso</option>
                </select>
                <input
                  inputMode="decimal"
                  value={value}
                  disabled={busy}
                  onChange={(e) => setValue(e.target.value)}
                  onBlur={() => save(kind, value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  className={INPUT}
                  aria-label="Provvigione"
                />
                {busy && <Loader className="h-3 w-3 animate-spin text-[#787774]" strokeWidth={1.5} />}
              </span>
            ) : (
              formatReferrerCommission(profile)
            )}
          </PropRow>
          <PropRow icon={<ArrowRight className="h-3 w-3" strokeWidth={1.5} />} label="Commissioni">
            <Link to={`/partners/${partner.id}/commissions`} className="text-[#2383E2] hover:underline">
              Vedi estratto conto
            </Link>
          </PropRow>
          <div className="px-1.5 pt-1 text-[11px] text-[rgba(55,53,47,0.45)] dark:text-white/40">
            Vale sulle offerte in cui il partner è indicato come segnalatore.
          </div>
        </div>
      ) : (
        <>
          <div className="px-1.5 text-[11px] text-[rgba(55,53,47,0.45)] dark:text-white/40">
            Questo partner non ha ancora il profilo provvigioni: non è selezionabile come segnalatore nelle offerte.
          </div>
          {canEditPartner && (
            <div
              role="button"
              tabIndex={0}
              onClick={busy ? undefined : handleCreate}
              onKeyDown={(e) => {
                if (!busy && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleCreate();
                }
              }}
              aria-disabled={busy}
              className={`flex items-center gap-1.5 px-1.5 py-1 rounded text-xs text-[#787774] dark:text-gray-400 ${
                busy
                  ? 'cursor-default opacity-70'
                  : 'cursor-pointer hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] hover:text-[#37352F] dark:hover:text-white'
              }`}
            >
              {busy ? <Loader className="h-3 w-3 animate-spin" strokeWidth={1.5} /> : <Plus className="h-3 w-3" strokeWidth={1.5} />}
              {busy ? 'Creazione in corso…' : 'Crea profilo provvigioni'}
            </div>
          )}
        </>
      )}
    </SectionToggle>
  );
}
